/**
 * Helpers for the "Download the app" nudge (see components/AppDownloadPrompt).
 * Kept out of the component file so that module only exports components
 * (react-refresh / fast-refresh requirement).
 */
import { getPlatform } from "@/lib/iap";

// Parrot on the App Store. Mirrors the canonical link used across landing/
// (e.g. landing/src/components/Hero.tsx). Keep in sync if the listing moves.
export const APP_STORE_URL =
  "https://apps.apple.com/ca/app/parrot-language-learning/id6736955121";

/**
 * True when the visitor is on an iOS device viewing the PWA in a real browser.
 * Broad device check (iOS Chrome reports a Safari-like UA, so we accept it);
 * iPadOS 13+ masquerades as desktop Safari, so we also catch touch-Macs.
 * Excludes the in-app webkit bridge via getPlatform() — there the user already
 * has the app, so prompting to download it would be nonsense.
 */
export function isIosWebVisitor(): boolean {
  if (typeof navigator === "undefined") return false;
  if (getPlatform() === "apple") return false; // already inside the native app
  const ua = navigator.userAgent || "";
  const isIosUa = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ presents as "MacIntel" desktop Safari but exposes touch points.
  const isIpadOs =
    navigator.platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return isIosUa || isIpadOs;
}

/**
 * True when this is iOS *Safari* specifically (not Chrome/Firefox/etc. on iOS).
 * Safari renders the NATIVE Smart App Banner from the apple-itunes-app meta tag
 * (with real install-detection + "Open"), so our custom banner stands down there
 * and only shows on the non-Safari iOS browsers that don't get the native one.
 */
export function isIosSafari(): boolean {
  if (!isIosWebVisitor()) return false;
  const ua = navigator.userAgent || "";
  // Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), Opera (OPiOS/OPT) on iOS.
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(ua);
  return /Safari/.test(ua) && !isOtherBrowser;
}
